"use client";

import { useEffect } from "react";

const STORAGE_KEY = "ad_tracking_data";
const VISIT_COUNT_KEY = "meta_visit_count";

/**
 * All tracking params we capture from the landing URL.
 * Stored as a JSON object in localStorage.
 */
export interface TrackingData {
  landing_url?: string;
  fbclid?: string;
  gclid?: string;
  fbp?: string;
  fbc?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  [key: string]: string | undefined; // allow any extra params
}

/** Params we actively capture from the URL */
const TRACKED_PARAMS = [
  "fbclid",
  "gclid",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
];

/**
 * Client component that captures ad tracking query params from the landing
 * page URL and stores them in localStorage. Replaces existing values with
 * new ones when URL params are present. Increments a visit counter only
 * when fbclid is present (Meta ad click) on the first page load.
 */
export function AdTrackingCapture() {
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);

      // Check if any tracked params exist in the current URL
      const newData: TrackingData = {};
      let hasTrackingParams = false;

      for (const key of TRACKED_PARAMS) {
        const val = params.get(key);
        if (val) {
          newData[key] = val;
          hasTrackingParams = true;
        }
      }

      if (hasTrackingParams) {
        // Always store the full landing URL
        newData.landing_url = window.location.href;

        // Merge with existing data (new values override old ones)
        const existing = getTrackingData();

        // fbclid and gclid are mutually exclusive click sources.
        // If a new fbclid arrives, clear any stale gclid (and vice versa)
        // to prevent incorrect CAPI skip logic.
        if (newData.fbclid) {
          delete existing.gclid;
        }
        if (newData.gclid) {
          delete existing.fbclid;
        }

        const merged = { ...existing, ...newData };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));

        // Increment Meta visit count only when fbclid is present
        if (newData.fbclid) {
          const currentCount = parseInt(
            localStorage.getItem(VISIT_COUNT_KEY) || "0",
            10
          );
          localStorage.setItem(VISIT_COUNT_KEY, String(currentCount + 1));
        }

        console.log("[AdTracking] Saved to localStorage:", merged);
      } else {
        console.log("[AdTracking] No tracking params in current URL");
      }
    } catch (e) {
      console.error("[AdTracking] Error:", e);
    }
  }, []);

  return null;
}

/**
 * Retrieve all stored tracking data from localStorage.
 */
export function getTrackingData(): TrackingData {
  if (typeof window === "undefined") return {};

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      return JSON.parse(raw) as TrackingData;
    }
  } catch (e) {
    console.error("[AdTracking] Error reading localStorage:", e);
  }
  return {};
}

/**
 * Get the Meta visit count (how many times user arrived from a Meta ad).
 */
export function getMetaVisitCount(): number {
  if (typeof window === "undefined") return 0;

  try {
    return parseInt(localStorage.getItem(VISIT_COUNT_KEY) || "0", 10);
  } catch {
    return 0;
  }
}
